import { jsx as _jsx } from "react/jsx-runtime";
import isEqual from 'lodash.isequal';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Observable } from 'windowed-observable';
import { ErrorCode } from '../../ErrorHandling/CustomErrorBoundary';
import { fetchUserByPost, getMD2Info, updateUser } from '../../apis/settings.api';
import { getMerchantInfo, getSSOUser, logout, getSelfServeMerchantList, getBusinessInfo, } from '../../apis/user.api';
import { getToken } from '../../apis/user.api';
import { routes } from '../../config/routes.config';
import { redirectToLogOut } from '../../utils/browser-helpers';
import { AuthCookie, setCookie } from '../../utils/cookieJar';
import { getSessionPath } from './helper';
const observable = new Observable('globalState');
const LangObservable = new Observable('language');
export const GlobalStateContext = createContext(undefined);
// Creating a custom hook to access the context
export function useGlobalStateContext() {
    const context = useContext(GlobalStateContext);
    if (!context) {
        throw new Error('useGlobalStateContext must be used within an useGlobalStateContextProvider');
    }
    return context;
}
const getMerchant = (merchantList, merchantNumber) => {
    const onboardedMerchants = merchantList.filter((m) => m?.productOnboardingStatus);
    if (onboardedMerchants) {
        if (merchantNumber) {
            return (onboardedMerchants.find((m) => Number(m.number) === Number(merchantNumber)) ??
                merchantList[0]);
        }
    }
};
export const GlobalStateContextProvider = ({ children, initialGlobalState }) => {
    const [globalState, setGlobalState] = useState(initialGlobalState);
    const [errorState, setErrorState] = useState();
    const { i18n: { resolvedLanguage = 'en' } = {}, i18n } = useTranslation();
    // Observer function that will keep our local globalState in sync
    const observer = (state) => {
        if (!isEqual(state, globalState)) {
            setGlobalState(state);
        }
    };
    const navigate = useNavigate();
    const { mylocations } = routes;
    // Setup sub
    useEffect(() => {
        observable.subscribe(observer);
        return () => {
            observable.unsubscribe(observer);
        };
    }, []);
    const updateGlobalState = (values) => {
        setGlobalState((prevState) => {
            observable.publish({ ...prevState, ...values });
            return { ...prevState, ...values };
        });
    };
    const showSnackBar = (message, snackBarType = 'success') => {
        setGlobalState({ ...globalState, displaySnackBar: true, snackBarMsg: message, snackBarType });
    };
    const hideSnackBar = () => {
        setGlobalState({ ...globalState, displaySnackBar: false, snackBarMsg: '' });
    };
    const setLoadedScrollSections = (sectionStatus) => {
        updateGlobalState({ ...globalState, loadedScrollSections: sectionStatus });
    };
    const globalStateAPIEndPoint = {
        getUserMerchant: '/userMerchant/getInfo',
        updateUserMerchant: '/userMerchant',
        getMD2Info: '/userMerchant/details',
    };
    useEffect(() => {
        handleSessionHash();
        initUser();
    }, []);
    useEffect(() => {
        if (!globalState.isAuthenticated)
            return;
        if (globalState?.merchantList?.length === 0) {
            // No merchant available for this account
            setErrorState(ErrorCode.FORBIDDEN);
            return;
        }
        // Reset error state
        setErrorState(undefined);
        if (globalState?.merchant?.number === undefined) {
            // No merchant selected yet
            navigate(mylocations);
        }
    }, [globalState]);
    useEffect(() => {
        if (errorState === ErrorCode.UNAUTHORIZED ||
            globalState?.globalError === ErrorCode.UNAUTHORIZED) {
            // flash unauthorized error page before logging user out
            setErrorState(ErrorCode.UNAUTHORIZED);
            setTimeout(() => handleLogout(redirectToLogOut), 3000);
        }
    }, [errorState, globalState?.globalError]);
    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        LangObservable.publish(lang);
    };
    const handleSessionHash = () => {
        if (window.location.hash.length) {
            sessionStorage.setItem('sessionPath', getSessionPath());
        }
        else if (sessionStorage.getItem('sessionPath') !== null) {
            const sessionPath = sessionStorage.getItem('sessionPath');
            sessionStorage.removeItem('sessionPath');
            navigate(sessionPath, { replace: true });
        }
    };
    const processSelfServeMerchantList = (res) => {
        updateGlobalState({
            selfServeMerchants: res,
        });
        // getting / setting the auth token here after user has been authenticated through SSO
        getToken().then((token) => {
            setCookie(AuthCookie.AUTH_TOKEN, token);
        });
    };
    const processError = (error) => {
        console.error(error);
        setErrorState(error.response?.status || ErrorCode.ERROR);
    };
    const initUser = () => {
        updateGlobalState({ loading: true });
        getSSOUser()
            .then((ssoUsername) => {
            if (ssoUsername)
                localStorage.setItem('idi-merchant-username', ssoUsername.toLowerCase());
            if (errorState)
                setErrorState(undefined);
            getMerchantInfo()
                .then((merchantList) => {
                const merchNum = localStorage.getItem('idi-merchant-no');
                if (merchantList.length === 0) {
                    return;
                }
                const merchant = getMerchant(merchantList, merchNum);
                if (merchant) {
                    // localStorage.setItem('idi-merchant-no', merchant?.number?.toString() || '');
                    changeLanguage(merchant?.displayLanguage || resolvedLanguage);
                }
                getSelfServeMerchantList(merchantList)
                    .then(processSelfServeMerchantList)
                    .catch(processError);
                updateGlobalState({
                    isAuthenticated: true,
                    loading: false,
                    merchant,
                    merchantList,
                });
            })
                .catch(processError);
        })
            .catch((e) => {
            if (e?.message === '401-Reauthenticate') {
                setErrorState(411); // should probably just do loading or instead of error. Need to check suspense logic
            }
            else {
                setErrorState(e.response?.status || 1);
            }
        });
    };
    useEffect(() => {
        if (globalState?.merchant?.merchantNumber === undefined)
            return;
        getBusinessInfo(globalState?.merchant?.merchantNumber)
            .then((res) => {
            updateGlobalState({
                merchant: {
                    ...globalState.merchant,
                    businessName: res?.businessName,
                    businessLegalName: res?.businessLegalName,
                },
            });
        })
            .catch(processError);
    }, [globalState?.merchant?.merchantNumber]);
    const fetchUser = async () => {
        if (!globalState?.merchant?.number) {
            return;
        }
        try {
            const res = await fetchUserByPost({
                merchantNumber: globalState?.merchant?.number,
            }, globalStateAPIEndPoint.getUserMerchant);
            const response = res;
            const { preferredName, notifOnboardingStatus, productOnboardingStatus, notifLanguage, displayLanguage, } = response || {};
            updateGlobalState({
                merchant: {
                    ...globalState.merchant,
                    preferredName,
                    notifOnboardingStatus,
                    productOnboardingStatus,
                    notifLanguage,
                    displayLanguage,
                },
            });
            changeLanguage(displayLanguage || resolvedLanguage);
        }
        catch (error) {
            console.log(error);
        }
    };
    const fetchMD2Info = async () => {
        if (!globalState?.merchant?.number) {
            return;
        }
        try {
            const res = await getMD2Info({
                merchantNum: globalState?.merchant?.number,
            }, globalStateAPIEndPoint.getMD2Info);
            const response = res;
            updateGlobalState({
                md2Info: response,
            });
        }
        catch (error) {
            console.log(error);
        }
    };
    const updateUserContext = async (values) => {
        if (!globalState?.merchant?.number) {
            return;
        }
        try {
            const res = await updateUser({
                merchantNumber: globalState?.merchant?.number,
                ...values,
            }, globalStateAPIEndPoint.updateUserMerchant);
            const response = res;
            const { result, success } = response || {};
            changeLanguage(result?.displayLanguage || resolvedLanguage);
            updateGlobalState({
                merchantUpdated: success,
                merchant: {
                    ...globalState.merchant,
                    ...result,
                },
            });
        }
        catch (error) {
            console.log(error);
        }
    };
    const handleLogout = async (cb) => {
        try {
            logout().finally(() => {
                if (typeof cb === 'function')
                    cb();
            });
        }
        catch (e) {
            //if already logged out for example
            console.error(e);
            if (typeof cb === 'function')
                cb();
        }
    };
    const contextValue = {
        fetchUser,
        globalState,
        updateGlobalState,
        showSnackBar,
        hideSnackBar,
        setLoadedScrollSections,
        updateUserContext,
        fetchMD2Info,
        errorState,
        handleLogout,
    };
    return _jsx(GlobalStateContext.Provider, { value: contextValue, children: children });
};
